import React, { useEffect, useRef, useState } from 'react';

/**
 * Scroll-reveal primitive used across the landing page.
 *
 * The animation is opt-in rather than assumed: if the browser has no
 * IntersectionObserver, or the visitor asked for reduced motion, the content
 * starts visible and no observer is created. That matters because the hidden
 * state is `opacity: 0` — anything that could leave an element unobserved has
 * to leave it painted instead.
 */
const canAnimate = () => {
  if (typeof window === 'undefined') return false;
  if (!('IntersectionObserver' in window)) return false;
  return !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
};

export const useReveal = ({
  threshold = 0.12,
  rootMargin = '0px 0px -8% 0px',
} = {}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => !canAnimate());

  useEffect(() => {
    if (shown) return undefined;

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shown, threshold, rootMargin]);

  return { ref, shown };
};

/**
 * Wraps children in an element that fades and lifts into place once it
 * scrolls into view.
 *
 * `variant` picks the motion (`up` by default, plus `left`, `right`, `zoom`),
 * `delay` staggers siblings, and `stagger` instead animates the element's own
 * children one after another — useful for a rail of tiles where wrapping each
 * child would mean an observer per tile.
 */
const Reveal = ({
  as: Tag = 'div',
  variant = 'up',
  delay = 0,
  stagger = false,
  className = '',
  style,
  children,
  ...rest
}) => {
  const { ref, shown } = useReveal();

  const classes = [
    'ij-reveal',
    `ij-reveal--${variant}`,
    stagger && 'ij-reveal--stagger',
    shown && 'is-in',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag
      ref={ref}
      className={classes}
      style={delay ? { ...style, '--ij-reveal-delay': `${delay}ms` } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
